import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeIn, GoldButton, OutlineButton } from "@/components/Reveal";
import { EXPERIENCES } from "@/data/experiences";

function ExperienceCard({ exp, index }) {
  return (
    <FadeIn delay={index * 0.1} className="h-full">
      <Link
        to={`/experiences/${exp.slug}`}
        data-testid={`experiences-page-card-${index + 1}`}
        className="group flex h-full flex-col border border-white/5 bg-navy-800 transition-colors duration-500 hover:border-gold/40"
      >
        <div className="relative aspect-[3/4] overflow-hidden">
          <img
            src={exp.cardImage}
            alt={exp.title}
            className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.07]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/10 to-navy-950/20" />
          <span className="absolute right-4 top-4 bg-gold px-3 py-1.5 text-[11px] font-semibold tracking-[0.12em] text-navy-800">
            {exp.details.find((d) => d.label === "Price")?.value}
          </span>
          <span className="absolute left-4 top-4 border border-gold/50 bg-navy-950/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-gold backdrop-blur-sm">
            {exp.tag}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-serif text-2xl leading-tight text-ivory">{exp.title}</h3>
          <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-mist">{exp.short}</p>
          <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4 text-[11px] uppercase tracking-[0.18em] text-ivory/60">
            <span>{exp.details.find((d) => d.label === "Duration")?.value}</span>
            <span>{exp.details.find((d) => d.label === "Max capability")?.value}</span>
          </div>
          <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
            View Details
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </div>
      </Link>
    </FadeIn>
  );
}

export default function ExperiencesPage() {
  return (
    <>
      <PageHero
        eyebrow="Tours & Experiences"
        title="Adventures for every explorer"
        image="/images/exp-sea-safari-hero.jpg"
      >
        Three ways to cross Clew Bay and one unbeatable way to warm up. Every tour is guided,
        kitted out and built around your group.
      </PageHero>

      <section className="bg-navy-950 py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {EXPERIENCES.map((exp, i) => (
              <ExperienceCard key={exp.slug} exp={exp} index={i} />
            ))}
          </div>
          <FadeIn delay={0.15}>
            <p className="mt-10 border-t border-white/10 pt-6 text-[12px] uppercase tracking-[0.18em] text-ivory/50">
              All tours depart from Rosmoney Pier, 12 minutes from Westport town &middot; add the
              Ultimate Private Island Picnic Tour to any trip for &euro;20pp extra
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-navy-800 py-20">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Eyebrow center>Book With Confidence</Eyebrow>
          <FadeIn delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-tight text-ivory sm:text-5xl">
              You are in <em className="italic text-gold">good hands</em>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-sm font-light leading-relaxed text-mist sm:text-base">
              Our capable team takes pride in curating unforgettable experiences from start to
              finish &mdash; safe, seamless and incredibly fun. Call +353 86 251 8252 or send an
              enquiry and we'll build your perfect day.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
              <GoldButton href="/contact" testId="experiences-page-cta">Enquire Now</GoldButton>
              <OutlineButton href="tel:+353862518252" testId="experiences-page-call">+353 86 251 8252</OutlineButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
